import type { ApiErrorMessageEntry } from '@repo/api'

/**
 * Where one Duplicate's save stands: `idle` before any save, `queued` behind the card being sent,
 * `unsent` when a save stopped before it. A refusal carries the API's error entries, shown on its
 * Duplicate, since the API's Stop errors name no Stop. A request sent without a response is
 * unconfirmed: the Load may still exist. `skipped` is an unconfirmed Duplicate the user chose not
 * to send again, so the cards after it can be sent.
 */
export type DuplicateSaveOutcome =
  | { errors: ApiErrorMessageEntry[]; status: 'failed' }
  | { loadId: number; status: 'created' }
  | { status: 'idle' }
  | { status: 'queued' }
  | { status: 'sending' }
  | { status: 'skipped' }
  | { status: 'unconfirmed' }
  | { status: 'unsent' }

export const IDLE: DuplicateSaveOutcome = { status: 'idle' }
const QUEUED: DuplicateSaveOutcome = { status: 'queued' }
const SENDING: DuplicateSaveOutcome = { status: 'sending' }
export const SKIPPED: DuplicateSaveOutcome = { status: 'skipped' }
const UNSENT: DuplicateSaveOutcome = { status: 'unsent' }

/** The new Loads, in card order. */
export const getCreatedLoadIds = (outcomes: DuplicateSaveOutcome[]) =>
  outcomes.flatMap(outcome => (outcome.status === 'created' ? [outcome.loadId] : []))

/** How many Duplicates were sent without a confirmed answer, skipped or not. */
export const getUnconfirmedCount = (outcomes: DuplicateSaveOutcome[]) =>
  outcomes.filter(({ status }) => status === 'unconfirmed' || status === 'skipped').length

/**
 * A created or skipped Duplicate is never sent again: the API creates a Load for every request, and
 * a skipped one may already have one.
 */
export const isSettled = (outcome: DuplicateSaveOutcome | undefined) =>
  outcome?.status === 'created' || outcome?.status === 'skipped'

/**
 * Sends each Duplicate not yet settled, one at a time in card order, and stops at the first one
 * that isn't created; the cards after it are left unsent. `report` receives every card's outcome
 * after each change; the result is the last.
 */
export const saveInCardOrder = async <Body>(
  bodies: Body[],
  previous: DuplicateSaveOutcome[],
  send: (_body: Body) => Promise<DuplicateSaveOutcome>,
  report: (_outcomes: DuplicateSaveOutcome[]) => void,
) => {
  let outcomes = bodies.map((_, index): DuplicateSaveOutcome => {
    const outcome = previous[index]
    return outcome && isSettled(outcome) ? outcome : QUEUED
  })

  for (const [index, body] of bodies.entries()) {
    if (isSettled(outcomes[index])) continue
    outcomes = outcomes.with(index, SENDING)
    report(outcomes)
    // One at a time, so every refusal belongs to a known Duplicate.
    const outcome = await send(body)
    outcomes = outcomes.with(index, outcome)
    if (outcome.status !== 'created') break
  }

  outcomes = outcomes.map(outcome => (outcome.status === 'queued' ? UNSENT : outcome))
  report(outcomes)
  return outcomes
}
